<script setup lang="ts">
import { useFishReconciliation } from "@/composables/tasks/fish-reconciliation/useFishReconciliation";
import { useNotifications } from "@/composables/useNotifications";
import FooterActions from "@/components/v2/Tasks/Vaccination/Buttons/ActionButtons/FooterActions.vue";
import { ref } from "vue";
import { Tank } from "@/types/v2";
import { TasksService } from "@/services/v2/tasks";

const { activeTask, tanks, navigateToTankView, isLoading, error, initTask } = useFishReconciliation();
const { showTaskCompletionSuccess, showError } = useNotifications();
const commentsModal = ref(false);
const instructionsModal = ref(false);

const toggleCommentsModal = () => commentsModal.value = !commentsModal.value;
const toggleInstructionsModal = () => instructionsModal.value = !instructionsModal.value;
const handleTankClick = (tank: Tank) => navigateToTankView(tank.key);

const handleCompleteTask = async () => {
    if (!activeTask.value) return;

    try {
        await TasksService.finishTask(activeTask.value);
        showTaskCompletionSuccess(activeTask.value.title);
    } catch (error) {
        console.error('Error completing task:', error);
        showError("Task Completion Failed", "Failed to complete task. Please try again.");
    }
};

const handleRetry = () => {
    initTask();
};
</script>

<template>
    <div v-if="isLoading" class="flex justify-center items-center h-full">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>

    <div v-else-if="error" class="flex flex-col justify-center items-center h-full">
        <div class="text-red-500 mb-4">{{ error }}</div>
        <button @click="handleRetry" class="px-4 py-2 bg-primary text-white rounded">Retry</button>
    </div>

    <div v-else class="flex flex-col h-full justify-between">
        <div class="space-y-6">
            <!-- Task title -->
            <div class="flex items-center justify-between border-b border-secondary pb-2">
                <h1 class="text-2xl font-bold text-gray-800">{{ activeTask?.title }} - Overview</h1>
                <span
                    v-if="activeTask?.studyTitle"
                    class="text-xs text-white bg-secondary opacity-80 px-2 py-1 rounded font-bold"
                >
                    {{ activeTask.studyTitle }}
                </span>
            </div>

            <!-- Tanks list -->
            <div class="space-y-3">
                <!-- Header -->
                <div class="grid grid-cols-12 px-4 py-2 bg-gray-50 rounded-t font-medium text-sm text-gray-600">
                    <div class="col-span-1">#</div>
                    <div class="col-span-5">Tank</div>
                    <div class="col-span-3">Status</div>
                    <div class="col-span-3">Actions</div>
                </div>

                <!-- Tanks -->
                <div
                    v-for="(tank, index) in tanks"
                    :key="tank.key"
                    class="grid grid-cols-12 px-4 py-3 bg-white border border-gray-100 rounded hover:bg-gray-50 cursor-pointer"
                    @click="handleTankClick(tank)"
                >
                    <div class="col-span-1 text-gray-500">{{ index + 1 }}</div>
                    <div class="col-span-5 font-medium">{{ tank.label }}</div>
                    <div class="col-span-3">
                        <span
                            class="px-2 py-1 text-xs font-semibold rounded-full"
                            :class="{
                                'bg-green-100 text-green-800': tank.status === 'completed',
                                'bg-yellow-100 text-yellow-800': tank.status === 'pending'
                            }"
                        >
                            {{ tank.status === 'completed' ? 'Completed' : 'Pending' }}
                        </span>
                    </div>
                    <div class="col-span-3">
                        <button
                            class="px-3 py-1 text-sm text-white bg-primary rounded hover:bg-primary-dark"
                            @click.stop="handleTankClick(tank)"
                        >
                            View Details
                        </button>
                    </div>
                </div>

                <!-- Empty state -->
                <div v-if="tanks.length === 0" class="py-8 text-center text-gray-500">
                    No tanks available for this task.
                </div>
            </div>
        </div>

        <!-- Footer Actions -->
        <div class="mt-auto">
            <FooterActions>
                <template #start>
                    <button
                        @click="toggleInstructionsModal"
                        class="w-full"
                    >
                        Instructions
                    </button>
                </template>
                <template #middle>
                    <button
                        @click="toggleCommentsModal"
                        class="w-full"
                    >
                        Comment
                    </button>
                </template>
                <template #end>
                    <button
                        @click="handleCompleteTask"
                        class="w-full"
                    >
                        Complete task
                    </button>
                </template>
            </FooterActions>
        </div>
    </div>
</template>
